import type { IngestionState } from "./ingestion.types";

/**
 * Durable job vocabulary for uploads that can outlive the initiating dialog.
 * The job is domain-neutral; its file items carry the destination-specific
 * context needed by the eventual review adapter.
 */
export const UPLOAD_JOB_SOURCES = [
  "picker",
  "dropzone",
  "camera",
  "imported_record",
] as const;

export type UploadJobSource = (typeof UPLOAD_JOB_SOURCES)[number];

export const UPLOAD_JOB_SOURCE_CHANNELS = [
  "patient_ui",
  "texting_agent",
  "import",
] as const;

export type UploadJobSourceChannel =
  (typeof UPLOAD_JOB_SOURCE_CHANNELS)[number];

export const UPLOAD_JOB_INTENTS = [
  "file",
  "attach",
  "prefill",
  "extract",
  "dump_anything",
] as const;

export type UploadJobIntent = (typeof UPLOAD_JOB_INTENTS)[number];

// TODO: replace these compatibility types with generated API types after the
// Dashboard API source-context contract is deployed and the OpenAPI package is
// regenerated.
export type UploadJobSourceContext = {
  surface?: string | null;
  route?: string | null;
  form?: string | null;
  messageId?: string | null;
  attachmentId?: string | null;
  targetHints?: string[];
  cycleId?: string | null;
  appointmentId?: string | null;
  specimenId?: string | null;
  providerId?: string | null;
  policyId?: string | null;
  paymentId?: string | null;
  quoteId?: string | null;
  allowBackgroundProcessing?: boolean;
  allowAutomaticPromotion?: boolean;
  locale?: string | null;
  language?: string | null;
};

export const UPLOAD_DESTINATIONS = [
  "document",
  "lab_result",
  "imaging_result",
  "medication",
  "appointment",
  "insurance",
  "specimen",
  "quote",
  "other",
] as const;

export type UploadDestination = (typeof UPLOAD_DESTINATIONS)[number];

export const UPLOAD_JOB_STATUSES = [
  "active",
  "handed_off",
  "review_ready",
  "partial_failure",
  "failed",
  "complete",
] as const;

export type UploadJobStatus = (typeof UPLOAD_JOB_STATUSES)[number];

export const UPLOAD_FAILURE_CODES = [
  "unsupported_type",
  "file_too_large",
  "batch_too_many_files",
  "batch_too_large",
  "empty_file",
  "corrupt_file",
  "password_protected",
  "duplicate",
  "network_stalled",
  "upload_failed",
  "extraction_failed",
  "persistence_failed",
  "canceled",
] as const;

export type UploadFailureCode = (typeof UPLOAD_FAILURE_CODES)[number];

export const UPLOAD_CHECKPOINTS = [
  "validation",
  "upload",
  "processing",
  "saving",
] as const;

export type UploadCheckpoint = (typeof UPLOAD_CHECKPOINTS)[number];

export type UploadProgress = {
  bytesSent: number;
  totalBytes: number | null;
  percent: number | null;
};

export type UploadFailure = {
  code: UploadFailureCode;
  checkpoint: UploadCheckpoint;
  retryable: boolean;
  /** Stable i18n key; user-facing copy must not come from backend text. */
  messageKey: string;
};

export type UploadFileItemSnapshot = {
  id: string;
  jobId: string;
  uploadId: string | null;
  name: string;
  contentType: string | null;
  sizeBytes: number | null;
  destination: UploadDestination;
  state: IngestionState;
  progress: UploadProgress | null;
  checkpoint: UploadCheckpoint;
  failure: UploadFailure | null;
  createdAt: string;
  updatedAt: string;
  lastEventSequence: number;
};

export type UploadJobSnapshot = {
  id: string;
  source: UploadJobSource;
  /** Optional until the generated API contract includes source context. */
  sourceChannel?: UploadJobSourceChannel;
  /** Optional until the generated API contract includes source context. */
  intent?: UploadJobIntent;
  /** Optional until the generated API contract includes source context. */
  sourceContext?: UploadJobSourceContext | null;
  status: UploadJobStatus;
  items: UploadFileItemSnapshot[];
  version: number;
  lastEventSequence: number;
  createdAt: string;
  updatedAt: string;
  handedOffAt: string | null;
  completedAt: string | null;
  acknowledgedAt: string | null;
};

export type UploadJobEventBase = {
  eventId: string;
  jobId: string;
  sequence: number;
  occurredAt: string;
  version: number;
};

export type UploadJobEvent =
  | (UploadJobEventBase & {
      type: "job.created";
      payload: {
        source: UploadJobSource;
        fileItemIds: string[];
      };
    })
  | (UploadJobEventBase & {
      type: "job.handed_off";
      payload: { reason: "size" | "duration" | "user_choice" };
    })
  | (UploadJobEventBase & {
      type: "file.state_changed";
      fileItemId: string;
      payload: {
        from: IngestionState;
        to: IngestionState;
        checkpoint: UploadCheckpoint;
      };
    })
  | (UploadJobEventBase & {
      type: "file.progressed";
      fileItemId: string;
      payload: { progress: UploadProgress };
    })
  | (UploadJobEventBase & {
      type: "file.failed";
      fileItemId: string;
      payload: {
        failure: UploadFailure;
        state: "failed_retryable" | "unsupported" | "rejected";
      };
    })
  | (UploadJobEventBase & {
      type: "file.retry_requested";
      fileItemId: string;
      payload: { fromCheckpoint: UploadCheckpoint };
    })
  | (UploadJobEventBase & {
      type: "file.canceled";
      fileItemId: string;
      payload: { checkpoint: UploadCheckpoint };
    })
  | (UploadJobEventBase & {
      type: "file.saved";
      fileItemId: string;
      payload: { destination: UploadDestination };
    })
  | (UploadJobEventBase & {
      type: "job.acknowledged";
      payload: { acknowledgedAt: string };
    });

export type UploadJobEventType = UploadJobEvent["type"];

export type ListUploadJobsRequest = {
  cursor?: string;
  limit?: number;
  includeCompleted?: boolean;
};

export type CreateUploadJobRequest = {
  clientRequestId: string;
  source: UploadJobSource;
  sourceChannel?: UploadJobSourceChannel;
  intent?: UploadJobIntent;
  sourceContext?: UploadJobSourceContext;
  items: {
    name: string;
    contentType?: string | null;
    sizeBytes?: number | null;
    destination: UploadDestination;
  }[];
};

export type AttachUploadJobSourceUploadRequest = {
  uploadId: string;
};

export type ListUploadJobsResponse = {
  jobs: UploadJobSnapshot[];
  nextCursor: string | null;
  serverTime: string;
};

export type GetUploadJobEventsRequest = {
  afterSequence?: number;
  limit?: number;
};

export type GetUploadJobEventsResponse = {
  jobId: string;
  events: UploadJobEvent[];
  nextSequence: number;
  hasMore: boolean;
  serverTime: string;
};

export type UploadJobCommand =
  | {
      type: "cancel_file";
      fileItemId: string;
      idempotencyKey: string;
    }
  | {
      type: "retry_file";
      fileItemId: string;
      idempotencyKey: string;
    }
  | {
      type: "acknowledge_job";
      idempotencyKey: string;
    };

export type UploadJobCommandResponse = {
  commandId: string;
  accepted: boolean;
  job: UploadJobSnapshot;
};

function isSuccessfulFileItem(item: UploadFileItemSnapshot): boolean {
  return item.state === "complete" || item.state === "source_only";
}

function isActiveFileItem(item: UploadFileItemSnapshot): boolean {
  return [
    "selecting",
    "validating",
    "queued",
    "uploading",
    "uploaded",
    "processing",
    "background_processing",
    "saving",
  ].includes(item.state);
}

/**
 * Derives the aggregate job status from durable file snapshots. This keeps a
 * batch from reporting success while one item is still recoverable or active.
 */
export function deriveUploadJobStatus(
  items: readonly UploadFileItemSnapshot[],
): UploadJobStatus {
  const visibleItems = items.filter((item) => item.state !== "canceled");
  if (visibleItems.length === 0) return "complete";

  const hasRetryableFailure = visibleItems.some(
    (item) => item.state === "failed_retryable",
  );
  const hasRejectedItem = visibleItems.some(
    (item) => item.state === "unsupported" || item.state === "rejected",
  );
  const hasActiveItem = visibleItems.some(isActiveFileItem);
  const hasBackgroundItem = visibleItems.some(
    (item) => item.state === "background_processing",
  );
  const hasPartialSuccess = visibleItems.some(
    (item) => item.state === "partial_success",
  );
  const hasReviewableItem = visibleItems.some(
    (item) => item.state === "review_ready" || item.state === "needs_review",
  );

  if (hasPartialSuccess) return "partial_failure";
  if (hasRetryableFailure || hasRejectedItem) {
    return visibleItems.some(
      (item) =>
        isSuccessfulFileItem(item) || hasActiveItem || hasReviewableItem,
    )
      ? "partial_failure"
      : "failed";
  }
  if (hasBackgroundItem) return "handed_off";
  if (hasActiveItem) return "active";
  if (hasReviewableItem) return "review_ready";
  return "complete";
}

export function isUploadJobEventType(
  value: string,
): value is UploadJobEventType {
  return [
    "job.created",
    "job.handed_off",
    "file.state_changed",
    "file.progressed",
    "file.failed",
    "file.retry_requested",
    "file.canceled",
    "file.saved",
    "job.acknowledged",
  ].includes(value);
}
